import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { adminLogin, isAuthenticated, loginWithTelegram, isTelegram, isAdmin, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingTelegram, setCheckingTelegram] = useState(true);

  // Ждём инициализацию Telegram WebApp
  useEffect(() => {
    const timer = setTimeout(() => {
      setCheckingTelegram(false);
    }, 1000); // Ждём 1 секунду для загрузки Telegram WebApp

    return () => clearTimeout(timer);
  }, []);

  // Если уже авторизован — редирект в кабинет или админку
  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Если открыто в Telegram — моментальная авторизация
  const handleTelegramLogin = useCallback(() => {
    console.log('Attempting Telegram login...');
    loginWithTelegram();
  }, [loginWithTelegram]);

  useEffect(() => {
    if (isTelegram && !isAuthenticated && !authLoading && !checkingTelegram) {
      console.log('Telegram detected, auto-logging in...');
      handleTelegramLogin();
    }
  }, [isTelegram, isAuthenticated, authLoading, checkingTelegram, handleTelegramLogin]);

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminLogin(username, password);
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.detail || (language === 'ru' ? 'Неверный логин или пароль' : "Noto'g'ri login yoki parol"));
    } finally {
      setLoading(false);
    }
  };

  // Показываем загрузку при проверке Telegram или при авторизации
  if (checkingTelegram || (isTelegram && !isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="text-center bg-white p-10 rounded-3xl shadow-2xl border-2 border-primary-100 max-w-sm w-full">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto mb-5"></div>
          <p className="text-gray-900 font-extrabold text-lg mb-2">
            {language === 'ru' ? 'Вход в личный кабинет...' : 'Kabinetga kirilmoqda...'}
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {language === 'ru' ? 'Проверка данных Telegram' : 'Telegram ma\'lumotlari tekshirilmoqda'}
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-primary-600 bg-primary-50 py-2 px-4 rounded-full">
            <span>✈️</span>
            <span className="font-semibold">{language === 'ru' ? 'Защищённое соединение' : 'Xavfsiz ulanish'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-gray-50 via-primary-50/30 to-gray-50">
      <div className="max-w-md w-full">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-xl transform hover:scale-105 transition-transform">
            <span className="text-4xl">🏠</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">
            {language === 'ru' ? 'Вход для администратора' : 'Administrator uchun kirish'}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            {language === 'ru' ? 'Панель управления Samuyjoy' : 'Samuyjoy boshqaruv paneli'}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-gray-100">
          <form onSubmit={handleAdminSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3.5 rounded-2xl text-sm font-bold flex items-center animate-shake">
                <span className="mr-2 text-lg">⚠️</span>
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-bold text-gray-700 mb-2">
                {language === 'ru' ? 'Логин' : 'Login'}
              </label>
              <input
                id="username"
                type="text"
                required
                className="input text-sm w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                {language === 'ru' ? 'Пароль' : 'Parol'}
              </label>
              <input
                id="password"
                type="password"
                required
                className="input text-sm w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full py-4 font-bold text-base shadow-lg hover:shadow-xl transition-all rounded-xl"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                  {language === 'ru' ? 'Вход...' : 'Kirilmoqda...'}
                </div>
              ) : (
                <>
                  <span className="mr-2">🔐</span>
                  {language === 'ru' ? 'Войти в панель' : 'Panelga kirish'}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t-2 border-gray-100 text-center">
            <div className="bg-gradient-to-r from-blue-50 to-primary-50 border-2 border-primary-100 rounded-2xl p-4">
              <p className="text-sm text-gray-700 font-semibold mb-1">
                🤖 {language === 'ru' ? 'Для обычных пользователей' : 'Oddiy foydalanuvchilar uchun'}
              </p>
              <p className="text-xs text-gray-500">
                {language === 'ru'
                  ? 'Откройте бота @samuyjoy_bot и войдите автоматически'
                  : "@samuyjoy_bot botini oching va avtomatik kiring"}
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8 font-medium">
          © 2026 Samuyjoy. {language === 'ru' ? 'Все права защищены' : 'Barcha huquqlar himoyalangan'}.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
